import React, { useState, useEffect } from 'react';
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import GeneralSettingsService from '../../../../services/GeneralSettingsService';
import Spinner from 'react-bootstrap/Spinner';

// Fonction de formatage des montants
const formatMoney = (amount, currency) => {
    if (amount === null || amount === undefined || amount === '') return 'N/A';

    const number = typeof amount === 'string'
        ? parseFloat(amount.replace(/\s/g, '').replace(',', '.'))
        : amount;

    if (isNaN(number)) return 'N/A';

    const hasDecimal = number % 1 !== 0;

    const formattedAmount = number.toLocaleString('fr-FR', {
        minimumFractionDigits: hasDecimal ? 2 : 0,
        maximumFractionDigits: hasDecimal ? 2 : 0
    });

    return currency ? `${formattedAmount} ${currency}` : formattedAmount;
};

const Step4Form = ({
                       formData,
                       handleChange,
                       onNext,
                       onBack,
                       recipientInfo,
                       selectedAccount
                   }) => {
    const [errors, setErrors] = useState({});
    const [isNextDisabled, setIsNextDisabled] = useState(true);
    const [displayValue, setDisplayValue] = useState("0");
    const [parameters, setParameters] = useState({
        exchangeRate: 0,
        bankDepositMinAmount: 0,
        bankDepositMaxAmount: 0,
        walletDepositMinAmount: 0,
        walletDepositMaxAmount: 0,
        countryRates: new Map()
    });
    const [loading, setLoading] = useState(true);

    // Détermine si le type de retrait est un wallet ou une banque
    const isWalletTransfer = () => {
        const method = formData.withdrawalMethod;
        return method === "MTN_MOBILE_MONEY" || method === "ORANGE_MONEY" || method === "WAVE" || method === "Wave" || method === "MOBILE MONEY" || method === "Orange Money";
    };

    useEffect(() => {
        const fetchParameters = async () => {
            try {
                const data = await GeneralSettingsService.getAllParameters();
                const firstParameter = data[0];

                // Créer une Map des taux par pays
                const ratesMap = new Map(
                    firstParameter.paramRates.map(rate => {
                        // Normaliser le nom du pays pour le Bénin
                        const normalizedCountry = rate.country.toLowerCase() === 'bénin' ? 'benin' : rate.country.toLowerCase();
                        return [
                            normalizedCountry,
                            {
                                rate: rate.rate,
                                maxAmount: rate.maxAmount || firstParameter.bankDepositMaxAmount
                            }
                        ];
                    })
                );

                // Normaliser le nom du pays sélectionné
                const selectedCountry = formData.country.toLowerCase() === 'bénin' ? 'benin' : formData.country.toLowerCase();

                // Obtenir le taux pour le pays sélectionné
                const selectedCountryRate = getCountryRate(selectedCountry, ratesMap);

                setParameters({
                    exchangeRate: selectedCountryRate.rate,
                    bankDepositMinAmount: firstParameter.bankDepositMinAmount,
                    bankDepositMaxAmount: firstParameter.bankDepositMaxAmount,
                    walletDepositMinAmount: firstParameter.walletDepositMinAmount,
                    walletDepositMaxAmount: firstParameter.walletDepositMaxAmount,
                    countryRates: ratesMap
                });
            } catch (error) {
                console.error('Erreur lors de la récupération des paramètres:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchParameters();
    }, [formData.country]);

    // Fonction pour obtenir le taux du pays sélectionné
    const getCountryRate = (country, ratesMap) => {
        return ratesMap.get(country) || {
            rate: 0,
            maxAmount: 0
        };
    };

    // Obtenir le montant minimum et maximum en fonction du type de dépôt
    const getMinAmount = () => {
        return isWalletTransfer() ? parameters.walletDepositMinAmount : parameters.bankDepositMinAmount;
    };

    const getMaxAmount = () => {
        return isWalletTransfer() ? parameters.walletDepositMaxAmount : parameters.bankDepositMaxAmount;
    };

    // Transformer les virgules en points
    const transformCommaToDot = (value) => {
        if (!value) return "0";
        return typeof value === 'string' ? value.replace(/,/g, ".") : value.toString();
    };

    // Validation du montant
    const validate = (amount) => {
        const newErrors = {};
        const numericAmount = parseFloat(transformCommaToDot(amount));
        const minAmount = getMinAmount();
        const maxAmount = getMaxAmount();
        const depositType = isWalletTransfer() ? "wallet" : "bancaire";

        if (!amount || isNaN(numericAmount) || numericAmount <= 0) {
            newErrors.transactionAmount = 'Veuillez entrer un montant valide.';
        } else if (numericAmount < minAmount) {
            newErrors.transactionAmount = `Le montant minimum pour un dépôt ${depositType} est de ${formatMoney(minAmount, 'CAD')}.`;
        } else if (numericAmount > maxAmount) {
            newErrors.transactionAmount = `Le montant maximum pour un dépôt ${depositType} est de ${formatMoney(maxAmount, 'CAD')}.`;
        }

        setErrors(newErrors);
        setIsNextDisabled(Object.keys(newErrors).length > 0);
        return Object.keys(newErrors).length === 0;
    };

    // Gestion du changement de valeur
    const handleInputChange = (e) => {
        let value = e.target.value;

        if (!/^[0-9,]*$/.test(value) && value !== '') {
            return;
        }

        value = value === "" ? "0" : value.startsWith("0") && value.length > 1 && value[1] !== ","
            ? value.substring(1)
            : value;

        setDisplayValue(value);
        handleChange({
            target: {
                name: "transactionAmount",
                value: value,
            },
        });

        // Mettre à jour également le montant converti
        const convertedAmount = (parseFloat(transformCommaToDot(value)) || 0) * parameters.exchangeRate;
        handleChange({
            target: {
                name: "convertedAmount",
                value: convertedAmount.toFixed(2),
            },
        });

        validate(value);
    };

    // Calcul du montant converti
    const getConvertedAmount = () => {
        const amount = parseFloat(transformCommaToDot(formData.transactionAmount)) || 0;
        return (amount * parameters.exchangeRate).toFixed(2);
    };

    // Gestion du clic sur Suivant
    const handleNext = () => {
        if (validate(formData.transactionAmount)) {
            // Mettre à jour le montant converti avant de passer à l'étape suivante
            const convertedAmount = getConvertedAmount();
            handleChange({
                target: {
                    name: "convertedAmount",
                    value: convertedAmount,
                },
            });

            // Stocker aussi la devise locale
            handleChange({
                target: {
                    name: "localCurrency",
                    value: formData.country === "Cameroun" ? "XAF" : "XOF",
                },
            });

            onNext(recipientInfo, selectedAccount);
        }
    };

    return (
        <Form noValidate className="step4-form">
            <div className="step4-form-fields-container">
                <Form.Group controlId="transactionAmount" className="step4-form-group">
                    <Form.Label className="step4-form-label">Entrez le montant à transférer (en CAD)</Form.Label>
                    <div className="step4-input-group">
                        <div className="step4-input-group-addon">CAD</div>
                        <Form.Control
                            type="text"
                            name="transactionAmount"
                            value={displayValue}
                            onChange={handleInputChange}
                            isInvalid={!!errors.transactionAmount}
                            className="step4-form-control"
                            disabled={loading}
                        />
                    </div>
                    {errors.transactionAmount && (
                        <Form.Control.Feedback type="invalid" className="step4-form-control-feedback">
                            {errors.transactionAmount}
                        </Form.Control.Feedback>
                    )}
                </Form.Group>

                {loading ? (
                    <div className="text-center">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Chargement...</span>
                        </Spinner>
                    </div>
                ) : (
                    <div className="step4-conversion-info">
                        <p>Taux de change : 1 CAD = {formatMoney(parameters.exchangeRate, formData.country === "Cameroun" ? "XAF" : "XOF")}</p>
                        <p>Montant converti : {formatMoney(getConvertedAmount(), formData.country === "Cameroun" ? "XAF" : "XOF")}</p>
                        <p>Type de dépôt : {isWalletTransfer() ? 'Wallet (Mobile Money)' : 'Bancaire'}</p>
                        <p>Montant minimum autorisé : {formatMoney(getMinAmount(), 'CAD')}</p>
                        <p>Montant maximum autorisé : {formatMoney(getMaxAmount(), 'CAD')}</p>
                    </div>
                )}
            </div>

            <div className="step4-form-actions">
                <Button variant="secondary" onClick={onBack} className="step4-btn step4-btn-secondary">
                    Retour
                </Button>
                <Button
                    variant="primary"
                    onClick={handleNext}
                    className="step4-btn step4-btn-primary"
                    disabled={isNextDisabled || loading}
                >
                    Suivant
                </Button>
            </div>
        </Form>
    );
};

export default Step4Form;